/**
 * The envelope page's two header cards (TMMAPP-0101; audit 06 § B2, v2 L1330–1362): "Categories in this
 * envelope" — a chip per category (swatch, name, signed all-time value) from the served
 * `allTimeCategoryTotals`, a dashed Uncategorized chip, "Manage in {virtual account} →" and the footnote;
 * categories are defined on the virtual account (MM-D-124), so nothing here adds, edits or deletes one —
 * and the Notes card. `contra` is served per part only, so a chip carries no marker.
 */
import { Box, Link, Paper, Stack, Typography, useTheme } from '@mui/material';
import { MoneyParts } from '@/components/MoneyText';
import { registerAmountColor } from '@/components/moneyTone';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AllTimeCategoryTotals } from '@/api/envelopes';
import { categoricalFor } from '@/theme/categorical';
import { resolveTokens } from '@/theme/theme';

const OVERLINE = {
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
} as const;

interface CategoriesInEnvelopeCardProps {
  totals: AllTimeCategoryTotals;
  currency: string | null;
  virtualAccountId: number;
  virtualAccountName: string;
}

export function CategoriesInEnvelopeCard({
  totals,
  currency,
  virtualAccountId,
  virtualAccountName,
}: CategoriesInEnvelopeCardProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const chip = (
    key: string | number,
    dashed: boolean,
    swatch: string | null,
    name: string,
    minor: number,
  ) => (
    <Box
      key={key}
      data-category-chip={dashed ? 'uncategorized' : key}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        border: `1px ${dashed ? 'dashed' : 'solid'} ${tokens.line}`,
        borderRadius: '4px',
        p: '6px 10px',
      }}
    >
      {swatch !== null && (
        <Box
          component="span"
          aria-hidden
          sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: swatch }}
        />
      )}
      <Typography component="span" sx={{ fontSize: 13 }}>
        {name}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: 13,
          fontVariantNumeric: 'tabular-nums',
          color: registerAmountColor(tokens, minor),
        }}
      >
        <MoneyParts minor={minor} currency={currency} signed />
      </Typography>
    </Box>
  );
  return (
    <Paper
      component="section"
      aria-labelledby="envelope-categories-title"
      sx={{ p: '14px 18px', display: 'flex', flexDirection: 'column', gap: '10px' }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}
      >
        <Typography
          id="envelope-categories-title"
          component="h2"
          sx={{ ...OVERLINE, color: tokens.textLabel('surface') }}
        >
          {t('envelope.categoriesInEnvelope')}
        </Typography>
        <Link
          component={RouterLink}
          to={`/v/${virtualAccountId}?tab=categories`}
          underline="hover"
          sx={{
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: tokens.link,
          }}
        >
          {t('envelope.manageIn', { virtualAccount: virtualAccountName })}
          <span aria-hidden>&nbsp;→</span>
        </Link>
      </Stack>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {totals.items.map((item) =>
          chip(
            item.categoryId,
            false,
            categoricalFor(item.categoryId, tokens).fill,
            item.name,
            item.amountMinor,
          ),
        )}
        {totals.uncategorizedMinor !== 0 &&
          chip('uncategorized', true, null, t('envelope.uncategorized'), totals.uncategorizedMinor)}
      </Box>
      <Typography sx={{ fontSize: 12, color: tokens.textLabel('surface') }}>
        {t('envelope.categoriesFootnote')}
      </Typography>
    </Paper>
  );
}

/** "Notes · full access only": the served `notes`, shown to every reader who is served one. */
export function NotesCard({ notes }: { notes: string }) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    <Paper component="section" aria-labelledby="envelope-notes-title" sx={{ p: '16px 20px' }}>
      <Stack direction="row" sx={{ alignItems: 'baseline', gap: 1 }}>
        <Typography
          id="envelope-notes-title"
          component="h2"
          sx={{ ...OVERLINE, color: tokens.textLabel('surface') }}
        >
          {t('envelope.notes')}
        </Typography>
        <Typography component="span" sx={{ fontSize: 11, color: tokens.textLabel('surface') }}>
          {t('envelope.notesFullAccess')}
        </Typography>
      </Stack>
      <Typography
        data-envelope-notes=""
        sx={{
          fontSize: 13,
          lineHeight: 1.7,
          mt: '7px',
          whiteSpace: 'pre-wrap',
          textWrap: 'pretty',
        }}
      >
        {notes}
      </Typography>
    </Paper>
  );
}
